import axios from 'axios'
import { config } from '../config'

const API_BASE_URL = config.apiUrl

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add auth token to requests
let getAccessToken: (() => Promise<string>) | null = null

export const setAccessTokenGetter = (getter: (() => Promise<string>) | null) => {
  getAccessToken = getter
}

api.interceptors.request.use(async (config) => {
  if (getAccessToken) {
    try {
      const token = await getAccessToken()
      config.headers.Authorization = `Bearer ${token}`
    } catch (error) {
      // Don't send requests without auth - unauthenticated 401s get
      // misinterpreted as "user needs onboarding" by the status hook
      return Promise.reject(error)
    }
  }
  return config
})

// API response types
export interface User {
  id: string
  email: string
  name: string
  role: string
  organizationId: string
  organizationName: string
  planName: string
  createdAt: string
  lastLoginAt?: string
}

export interface Project {
  id: string
  name: string
  description?: string
  documentsCount: number
  createdByName?: string
  createdAt: string
  updatedAt: string
  isOwner?: boolean
  isShared?: boolean
  sharedByName?: string
}

export interface ProjectShare {
  id: string
  userId: string
  userName: string
  userEmail: string
  sharedAt: string
}

export interface Document {
  id: string
  title: string
  type: string
  content: string
  caseId?: string
  clientId?: string
  projectId: string
  projectName?: string
  currentVersion: number
  pageCount?: number
  legalStateDate?: string
  sourcesUsed?: string
  createdByName?: string
  isGenerated?: boolean
  mimeType?: string
  fileSize?: number
  hasOriginalFile?: boolean
  createdAt: string
  updatedAt: string
}

export interface DocumentVersion {
  id: string
  version: number
  createdByName: string
  createdAt: string
  comment?: string
}

export interface Template {
  id: string
  name: string
  type: string
  documentType: string
  content: string
  variables?: string
  aiInstructions?: string
  description?: string
  currentVersion: number
  isSystem: boolean
  createdAt: string
  updatedAt: string
}

export interface Comment {
  id: string
  content: string
  selectionStart?: number
  selectionEnd?: number
  selectedText?: string
  userName: string
  createdAt: string
  isResolved: boolean
  resolvedAt?: string
  replies: Comment[]
}

export interface AuditLog {
  id: string
  action: string
  resourceType: string
  resourceId?: string
  userName: string
  userEmail: string
  ipAddress?: string
  isSuccess: boolean
  errorMessage?: string
  createdAt: string
}

export interface FeedbackReport {
  id: string
  type: string
  status: string
  description: string
  context?: string
  userEmail: string
  organizationName: string
  priority: number
  category?: string
  adminNotes?: string
  createdAt: string
  resolvedAt?: string
}

export interface SubscriptionInfo {
  id: string
  planName: string
  planDisplayName?: string
  status: string
  trialEndsAt?: string
  currentPeriodEnd?: string
  currentPeriodStart?: string
  documentsUsed: number
  documentsLimit: number
  pagesUsed: number
  pagesLimit: number
  tokensUsed: number
  tokensLimit: number
  usersCount: number
  usersLimit: number
  /** Days remaining in trial period (null if not on trial) */
  daysUntilTrialEnds?: number
  /** True if trial ends within 5 days */
  isTrialExpiringSoon: boolean
  /** Discount code applied to this subscription (null if none) */
  discountCode?: string | null
}

export interface AdminOrganization {
  id: string
  name: string
  nip?: string
  address?: string
  isActive: boolean
  planName: string
  planId?: string
  subscriptionId?: string
  status: string
  usersCount: number
  usersLimit: number
  projectsCount: number
  documentsCount: number
  documentsLimit: number
  pagesUsed?: number
  pagesLimit?: number
  tokensUsed: number
  tokensLimit: number
  trialEndsAt?: string
  currentPeriodEnd?: string
  documentsUsedThisPeriod?: number
  pagesUsedThisPeriod?: number
  aiTokensUsedThisPeriod?: number
  createdAt: string
  updatedAt: string
}

export interface AdminPlan {
  id: string
  name: string
  displayName: string
  maxUsers: number
  priceMonthly: number
}

export interface OrganizationStats {
  totalOrganizations: number
  activeOrganizations: number
  trialOrganizations: number
  newOrganizationsThisMonth: number
  organizationsByPlan: Record<string, number>
}

export interface PagedResult<T> {
  items: T[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
}

// Legal document types
export interface LegalDocumentInfo {
  slug: string
  displayName: string
  version: string
  effectiveDate: string
  webUrl?: string
}

// Onboarding types
export interface OnboardingStatus {
  exists: boolean
  requiresOnboarding: boolean
  requiresPlanSelection: boolean
  subscriptionStatus?: string
  trialEndsAt?: string
  planName?: string
  requiresConsentReacceptance?: boolean
  documentsRequiringReacceptance?: string[]
}

export interface RegisterRequest {
  organizationName: string
  userName?: string
  nip: string
  address?: string
}

export interface RegisterResponse {
  userId: string
  organizationId: string
  trialEndsAt?: string
  requiresPlanSelection: boolean
  message: string
}

export interface SelectPlanRequest {
  planId: string
  startTrial: boolean
}

export interface SelectPlanResponse {
  status: 'trial_started' | 'payment_required' | 'already_on_trial' | 'already_active' | 'trial_expired'
  trialEndsAt?: string
  planName?: string
  redirectTo?: string
}

export interface RegisterWithPlanRequest {
  email: string
  password: string
  organizationName: string
  userName?: string
  nip: string
  address?: string
  phoneNumber: string
  planId: string
  billingPeriod?: 'monthly' | 'yearly'
  discountCode?: string
  acceptTerms?: boolean
  acceptPrivacy?: boolean
  acceptB2B?: boolean
  acceptDPA?: boolean
  marketingEmail?: boolean
  marketingPhone?: boolean
}

export interface AcceptTermsRequest {
  acceptTerms: boolean
  acceptPrivacy: boolean
  acceptB2B: boolean
  acceptDPA: boolean
  marketingEmail: boolean
  marketingPhone: boolean
}

export interface RegisterWithPlanResponse {
  userId: string
  organizationId: string
  orderId: string
  paymentUrl: string
  amount: number
  currency: string
  proformaUrl?: string
  proformaNumber?: string
  message: string
}

export interface RegisterWithTrialResponse {
  userId: string
  organizationId: string
  trialEndsAt: string
  planName: string
  message: string
}

// API functions
export const authApi = {
  getProfile: () => api.get<User>('/auth/me'),
  register: (data: RegisterRequest) =>
    api.post<RegisterResponse>('/auth/register', data),
  recordLogin: () => api.post('/auth/login'),
  getStatus: () => api.get<OnboardingStatus>('/auth/status'),
  selectPlan: (data: SelectPlanRequest) =>
    api.post<SelectPlanResponse>('/auth/select-plan', data),
  /** Register new account + organization + payment order in one step (no auth required) */
  registerWithPlan: (data: RegisterWithPlanRequest) =>
    api.post<RegisterWithPlanResponse>('/auth/register-with-plan', data),
  /** Register new account + organization with trial (no auth required) */
  registerWithTrial: (data: RegisterWithPlanRequest) =>
    api.post<RegisterWithTrialResponse>('/auth/register-with-trial', data),
  /** Accept updated legal document terms (authorized) */
  acceptTerms: (data: AcceptTermsRequest) =>
    api.post('/auth/accept-terms', data),
}

export const legalDocumentsApi = {
  /** Get current versions of all legal documents (no auth required) */
  getCurrent: () => api.get<LegalDocumentInfo[]>('/legal-documents'),
  /** Get PDF download URL for a legal document */
  getPdfUrl: (slug: string) => `${API_BASE_URL}/legal-documents/${slug}/pdf`,
}

export const projectsApi = {
  list: () => api.get<Project[]>('/projects'),
  get: (id: string) => api.get<Project>(`/projects/${id}`),
  create: (data: { name: string; description?: string }) =>
    api.post<Project>('/projects', data),
  update: (id: string, data: { name?: string; description?: string }) =>
    api.put<Project>(`/projects/${id}`, data),
  delete: (id: string, force?: boolean) =>
    api.delete(`/projects/${id}${force ? '?force=true' : ''}`),
  getDocuments: (id: string, params?: { page?: number; pageSize?: number }) =>
    api.get<PagedResult<Document>>(`/projects/${id}/documents`, { params }),
  getShares: (id: string) => api.get<ProjectShare[]>(`/projects/${id}/shares`),
  addShare: (id: string, email: string) =>
    api.post<ProjectShare>(`/projects/${id}/shares`, { email }),
  removeShare: (id: string, shareId: string) =>
    api.delete(`/projects/${id}/shares/${shareId}`),
}

export const documentsApi = {
  list: (params?: { projectId?: string; caseId?: string; clientId?: string; type?: string; search?: string; page?: number }) =>
    api.get<PagedResult<Document>>('/documents', { params }),
  get: (id: string) => api.get<Document>(`/documents/${id}`),
  create: (data: Partial<Document>) => api.post<Document>('/documents', data),
  update: (id: string, data: Partial<Document>) =>
    api.put<Document>(`/documents/${id}`, data),
  delete: (id: string) => api.delete(`/documents/${id}`),
  getVersions: (id: string) => api.get<DocumentVersion[]>(`/documents/${id}/versions`),
  getDiff: (id: string, v1: number, v2: number) =>
    api.get(`/documents/${id}/diff`, { params: { v1, v2 } }),
  getComments: (id: string) => api.get<Comment[]>(`/documents/${id}/comments`),
  addComment: (id: string, data: { content: string; selectionStart?: number; selectionEnd?: number; selectedText?: string; parentCommentId?: string }) =>
    api.post<Comment>(`/documents/${id}/comments`, data),
  resolveComment: (documentId: string, commentId: string) =>
    api.post(`/documents/${documentId}/comments/${commentId}/resolve`),
  export: (id: string, format: 'docx' = 'docx') =>
    api.get(`/documents/${id}/export`, { params: { format }, responseType: 'blob' }),
  getFile: (id: string, download = false) =>
    api.get(`/documents/${id}/file`, { params: { download }, responseType: 'blob' }),
}

export const templatesApi = {
  list: (type?: string) => api.get<Template[]>('/templates', { params: { type } }),
  get: (id: string) => api.get<Template>(`/templates/${id}`),
  create: (data: Partial<Template>) => api.post<Template>('/templates', data),
  update: (id: string, data: Partial<Template>) =>
    api.put<Template>(`/templates/${id}`, data),
  delete: (id: string) => api.delete(`/templates/${id}`),
  getVersions: (id: string) => api.get(`/templates/${id}/versions`),
  restore: (id: string, version: number) =>
    api.post<Template>(`/templates/${id}/restore/${version}`),
}

export const feedbackApi = {
  submit: (data: { type: number; description: string; context?: object }) =>
    api.post('/feedback', data),
  getMy: () => api.get('/feedback/my'),
}

export const adminApi = {
  getFeedback: (params?: { status?: string; type?: string; page?: number }) =>
    api.get<PagedResult<FeedbackReport>>('/admin/feedback', { params }),
  updateFeedback: (id: string, data: { status?: number; priority?: number; adminNotes?: string }) =>
    api.put<FeedbackReport>(`/admin/feedback/${id}`, data),
  getFeedbackStats: () => api.get('/admin/feedback/stats'),
  getAuditLogs: (params?: { action?: string; resourceType?: string; userId?: string; from?: string; to?: string; page?: number }) =>
    api.get<PagedResult<AuditLog>>('/audit', { params }),
  getAuditStats: (days?: number) => api.get('/audit/stats', { params: { days } }),
  exportAuditLogs: (from?: string, to?: string) =>
    api.get('/audit/export', { params: { from, to }, responseType: 'blob' }),

  // Organizations
  listOrganizations: (params?: { search?: string; isActive?: boolean; planId?: string; page?: number; pageSize?: number }) =>
    api.get<PagedResult<AdminOrganization>>('/admin/organizations', { params }),
  getOrganization: (id: string) =>
    api.get<AdminOrganization>(`/admin/organizations/${id}`),
  createOrganization: (data: { name: string; planId: string; nip?: string; address?: string; firstUserEmail?: string; firstUserName?: string }) =>
    api.post<AdminOrganization>('/admin/organizations', data),
  updateOrganization: (id: string, data: { name?: string; nip?: string; address?: string; planId?: string; isActive?: boolean }) =>
    api.put<AdminOrganization>(`/admin/organizations/${id}`, data),
  deleteOrganization: (id: string) =>
    api.delete(`/admin/organizations/${id}`),
  getOrganizationUsers: (id: string) =>
    api.get<User[]>(`/admin/organizations/${id}/users`),
  getOrganizationStats: () =>
    api.get<OrganizationStats>('/admin/organizations/stats'),
  getPlans: () =>
    api.get<AdminPlan[]>('/admin/organizations/plans'),

  // Discount Codes
  listDiscountCodes: (params?: { isActive?: boolean; representativeId?: string; page?: number; pageSize?: number }) =>
    api.get<PagedResult<AdminDiscountCode>>('/admin/discount-codes', { params }),
  getDiscountCode: (id: string) =>
    api.get<AdminDiscountCode>(`/admin/discount-codes/${id}`),
  createDiscountCode: (data: CreateDiscountCodeRequest) =>
    api.post('/admin/discount-codes', data),
  updateDiscountCode: (id: string, data: UpdateDiscountCodeRequest) =>
    api.put(`/admin/discount-codes/${id}`, data),
  deleteDiscountCode: (id: string) =>
    api.delete(`/admin/discount-codes/${id}`),
  getDiscountCodeUsage: (id: string) =>
    api.get(`/admin/discount-codes/${id}/usage`),
  // Representatives
  listRepresentatives: (params?: { page?: number; pageSize?: number }) =>
    api.get<PagedResult<AdminRepresentative>>('/admin/representatives', { params }),
  getRepresentative: (id: string) =>
    api.get<AdminRepresentative>(`/admin/representatives/${id}`),
  createRepresentative: (data: CreateRepresentativeRequest) =>
    api.post('/admin/representatives', data),
  updateRepresentative: (id: string, data: UpdateRepresentativeRequest) =>
    api.put(`/admin/representatives/${id}`, data),
  getRepresentativeCustomers: (id: string) =>
    api.get(`/admin/representatives/${id}/customers`),
  getRepresentativeCommissions: (id: string, params?: { year?: number; month?: number; status?: string }) =>
    api.get(`/admin/representatives/${id}/commissions`, { params }),
  getRepresentativeSummary: (id: string, params?: { year?: number; month?: number }) =>
    api.get(`/admin/representatives/${id}/summary`, { params }),
  approveCommission: (repId: string, commId: string) =>
    api.post(`/admin/representatives/${repId}/commissions/${commId}/approve`),
  payCommission: (repId: string, commId: string) =>
    api.post(`/admin/representatives/${repId}/commissions/${commId}/pay`),

  // Error logs
  getErrors: (params?: { severity?: string; isResolved?: boolean; from?: string; to?: string; page?: number; pageSize?: number }) =>
    api.get<PagedResult<ErrorLogItem>>('/admin/errors', { params }),
  getErrorDetail: (id: string) => api.get<ErrorLogDetail>(`/admin/errors/${id}`),
  resolveError: (id: string) => api.put(`/admin/errors/${id}/resolve`),
  getErrorStats: () => api.get<ErrorStats>('/admin/errors/stats'),
}

// Discount Code types
export interface AdminDiscountCode {
  id: string
  code: string
  discountType: string
  discountValue: number
  durationKind: string
  durationMonths?: number
  expiresAt?: string
  maxUses?: number
  timesUsed: number
  isActive: boolean
  representativeId?: string
  representativeName?: string
  allowedPlanNames?: string
  notes?: string
  createdAt: string
}

export interface CreateDiscountCodeRequest {
  code: string
  discountType: string
  discountValue: number
  durationKind?: string
  durationMonths?: number
  expiresAt?: string
  maxUses?: number
  representativeId?: string
  allowedPlanNames?: string
  notes?: string
}

export interface UpdateDiscountCodeRequest {
  isActive?: boolean
  maxUses?: number
  expiresAt?: string
  notes?: string
  representativeId?: string
  allowedPlanNames?: string
}

// Representative types
export interface AdminRepresentative {
  id: string
  name: string
  email: string
  phoneNumber?: string
  companyName?: string
  commissionRate: number
  isActive: boolean
  activeCustomersCount: number
  totalCodesCount: number
  notes?: string
  createdAt: string
  updatedAt: string
}

export interface CreateRepresentativeRequest {
  name: string
  email: string
  phoneNumber?: string
  companyName?: string
  commissionRate: number
  notes?: string
}

export interface UpdateRepresentativeRequest {
  name?: string
  email?: string
  phoneNumber?: string
  companyName?: string
  commissionRate?: number
  isActive?: boolean
  notes?: string
}

export interface CommissionRecord {
  id: string
  organizationId: string
  organizationName: string
  paymentOrderId: string
  invoiceAmount: number
  commissionRate: number
  commissionAmount: number
  periodStart: string
  periodEnd: string
  status: string
  createdAt: string
  approvedAt?: string
  paidAt?: string
  notes?: string
}

export interface RepresentativeSummary {
  totalRevenue: number
  totalCommission: number
  pendingCommission: number
  approvedCommission: number
  paidCommission: number
  customerBreakdown: {
    organizationId: string
    organizationName: string
    totalRevenue: number
    totalCommission: number
    paymentsCount: number
  }[]
}

// Error log types
export interface ErrorLogItem {
  id: string
  exceptionType: string
  message: string
  severity: string
  requestMethod?: string
  requestPath?: string
  isResolved: boolean
  createdAt: string
}

export interface ErrorLogDetail extends ErrorLogItem {
  stackTrace?: string
  innerException?: string
  requestQuery?: string
  userId?: string
  organizationId?: string
  userAgent?: string
  ipAddress?: string
}

export interface ErrorStats {
  totalErrors: number
  unresolvedCount: number
  errorsByDay: Record<string, number>
  errorsBySeverity: Record<string, number>
}

export const subscriptionApi = {
  getCurrent: () => api.get<SubscriptionInfo>('/subscriptions/current'),
  getUsage: () => api.get('/subscriptions/usage'),
  cancel: () => api.post('/subscriptions/cancel'),
  applyDiscountCode: (code: string) => api.post('/subscriptions/apply-discount-code', { code }),
}

// Payment types
export interface Plan {
  id: string
  name: string
  description?: string
  priceMonthly: number
  priceYearly?: number
  maxUsers: number
  maxDocumentsPerPeriod: number
  maxPagesPerPeriod: number
  aiTokensMonthly: number
  features?: string
}

export interface PaymentOrder {
  id: string
  planId: string
  billingPeriod: string
  amount: number
  currency: string
  status: string
  createdAt: string
  paidAt?: string
  invoiceNumber?: string
}

export interface CreateOrderRequest {
  planId: string
  billingPeriod: 'monthly' | 'yearly'
  discountCode?: string
}

export interface CreateOrderResponse {
  orderId: string
  paymentUrl: string
  amount: number
  currency: string
  originalAmount?: number
  discountAmount?: number
  proformaUrl?: string
  proformaNumber?: string
}

export interface ValidateDiscountRequest {
  code: string
  planId: string
  billingPeriod: string
}

export interface ValidateDiscountResponse {
  isValid: boolean
  errorMessage?: string
  discountType?: string
  discountValue?: number
  description?: string
  originalPrice?: number
  finalPrice?: number
}

export interface ConfirmMockPaymentRequest {
  orderId: string
  invoiceId: string
}

export const paymentsApi = {
  getPlans: () => api.get<Plan[]>('/payments/plans'),
  createOrder: (data: CreateOrderRequest) =>
    api.post<CreateOrderResponse>('/payments/create-order', data),
  validateDiscount: (data: ValidateDiscountRequest) =>
    api.post<ValidateDiscountResponse>('/payments/validate-discount', data),
  getOrders: () => api.get<PaymentOrder[]>('/payments/orders'),
  getOrder: (id: string) => api.get<PaymentOrder>(`/payments/orders/${id}`),
  /** Check order payment status (anonymous, no auth needed) */
  getOrderStatus: (id: string) =>
    api.get<{ orderId: string; status: string; paidAt: string | null }>(`/payments/orders/${id}/status`),
  /** Confirm mock payment (development only) */
  confirmMockPayment: (data: ConfirmMockPaymentRequest) =>
    api.post('/payments/confirm-mock-payment', data),
  /** Activate trial fallback when payment fails (no auth required) */
  activateTrialFallback: (orderId: string) =>
    api.post('/payments/activate-trial-fallback', { orderId }),
}

// File upload types
export interface FileUploadResult {
  filename: string
  status: 'success' | 'error' | 'skipped' | 'empty'
  text: string
  textLength: number
  ocrUsed: boolean
  error: string | null
  fileId?: string
  pageCount: number
}

export interface UploadResponse {
  ok: boolean
  filesProcessed: number
  filesSuccessful: number
  totalTextLength: number
  results: FileUploadResult[]
}

export interface OcrStatus {
  ocrAvailable: boolean
  pdfOcrAvailable: boolean
  pypdfAvailable: boolean
  supportedExtensions: string[]
  imageExtensions: string[]
  error: string | null
}

export const uploadApi = {
  uploadFiles: (files: File[], useOcr = true) => {
    const formData = new FormData()
    files.forEach(file => formData.append('files', file))
    formData.append('use_ocr', String(useOcr))
    return api.post<UploadResponse>('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  getOcrStatus: () => api.get<OcrStatus>('/ocr/status'),
}

// Opinion generation types
export interface SourceItem {
  type: 'eli' | 'eurlex' | 'case_law' | 'uodo' | 'uzp' | 'sn'
  title: string
  url?: string
  act_id?: string
  article_num?: string
  court?: string
  signature?: string
  date?: string
  summary?: string
}

export interface OpinionMetadata {
  // .NET API returns camelCase
  legalStateDate?: string
  sourcesUsed?: string[]
  citationsVerified?: number
  citationsTotal?: number
  generationTimeMs?: number
  detailedSources?: SourceItem[]
  sessionId?: string
  // snake_case fallbacks (for direct gRPC/Python responses)
  legal_state_date?: string
  sources_used?: string[]
  citations_verified?: number
  citations_total?: number
  generation_time_ms?: number
  detailed_sources?: SourceItem[]
  session_id?: string
}

export interface OpinionChunk {
  text: string
  is_final: boolean
  metadata?: OpinionMetadata
  status?: string
}

export interface OpinionResponse {
  chunks: OpinionChunk[]
  full_text: string
  metadata?: OpinionMetadata
}

export interface OpinionVersion {
  version: number
  opinion: string
  instructions: string
  feedback?: string
  timestamp: string
  citationsVerified: number
  citationsTotal: number
}

export interface HistoryResponse {
  sessionId: string
  versions: OpinionVersion[]
  currentVersion: number
}

export interface ConversationState {
  session_id: string
  status: 'generating' | 'awaiting_feedback' | 'completed' | 'error'
  current_opinion?: string
  current_version: number
  sources?: {
    articles: SourceItem[]
    eurlex: SourceItem[]
    judgments: SourceItem[]
    uodo: SourceItem[]
    uzp: SourceItem[]
    sn: SourceItem[]
  }
  citations_verified: number
  citations_total: number
  error?: string
}

export interface StartConversationRequest {
  query: string
  instructions?: string
  document_context?: string
  role?: string
  doc_type?: string
  use_eurlex?: boolean
  use_uodo?: boolean
  use_case_law?: boolean
  use_uzp?: boolean
  use_sn?: boolean
  use_anonymization?: boolean
  projectId?: string
}

export interface ContinueConversationRequest {
  session_id: string
  action: 'accept' | 'revise' | 'new_query'
  feedback?: string
  new_instructions?: string
}

// Opinion session types (persistent DB sessions)
export interface OpinionSessionListItem {
  id: string
  title: string
  query: string
  status: string
  role: string
  docType: string
  currentVersion: number
  createdByName?: string
  resultDocumentId?: string
  createdAt: string
  updatedAt: string
}

export interface OpinionSessionTurnDetail {
  id: string
  version: number
  type: string
  userFeedback?: string
  opinionText: string
  metadata?: string
  sourcesUsed?: string
  citationsVerified: number
  citationsTotal: number
  createdAt: string
}

export interface OpinionSessionDetail {
  id: string
  externalSessionId?: string
  projectId: string
  title: string
  query: string
  instructions?: string
  role: string
  docType: string
  status: string
  currentVersion: number
  sourceConfig?: string
  resultDocumentId?: string
  createdByName?: string
  createdAt: string
  updatedAt: string
  turns: OpinionSessionTurnDetail[]
}

export interface CreateOpinionSessionRequest {
  projectId: string
  query: string
  instructions?: string
  role?: string
  docType?: string
  sourceConfig?: string
}

export interface AddTurnRequest {
  opinionText: string
  type?: string
  userFeedback?: string
  externalSessionId?: string
  metadata?: string
  sourcesUsed?: string
  citationsVerified: number
  citationsTotal: number
  documentContext?: string
}

export const opinionSessionsApi = {
  list: (projectId: string) =>
    api.get<OpinionSessionListItem[]>('/opinionsessions', { params: { projectId } }),
  get: (id: string) =>
    api.get<OpinionSessionDetail>(`/opinionsessions/${id}`),
  create: (data: CreateOpinionSessionRequest) =>
    api.post<OpinionSessionDetail>('/opinionsessions', data),
  update: (id: string, data: {
    title?: string; status?: string; resultDocumentId?: string;
    query?: string; instructions?: string; role?: string; docType?: string; sourceConfig?: string;
  }) => api.put(`/opinionsessions/${id}`, data),
  delete: (id: string) =>
    api.delete(`/opinionsessions/${id}`),
  addTurn: (id: string, data: AddTurnRequest) =>
    api.post<OpinionSessionTurnDetail>(`/opinionsessions/${id}/turns`, data),
}

// Opinion generation API
export const opinionApi = {
  // Simple generation (no conversation)
  generate: (data: StartConversationRequest) =>
    api.post<OpinionResponse>('/generation/opinion', data),

  // Conversation-based generation
  startConversation: (data: StartConversationRequest) =>
    api.post<ConversationState>('/conversation/start', data),

  continueConversation: (data: ContinueConversationRequest) =>
    api.post<ConversationState>('/conversation/continue', data),

  getConversation: (sessionId: string) =>
    api.get<ConversationState>(`/conversation/${sessionId}`),

  getSources: (sessionId: string) =>
    api.get<{ session_id: string; sources: SourceItem[] }>(`/conversation/${sessionId}/sources`),

  // Version history
  getHistory: (sessionId: string) =>
    api.get<HistoryResponse>(`/generation/history/${sessionId}`),
}
